<template>
  <div class="card">
    <div class="card-header">
      <div class="container-fluid">
        <div class="row d-flex justify-content-between">
          <div>
            <h6 class="mb-0 mt-2">
              <span class="badge" :class="statusCssClass">{{ investigation.statusShortDescription }}</span> <b>{{ header }}</b>
            </h6>
          </div>
          <div>
            <ul class="nav nav-pills card-header-pills justify-content-end">
              <li v-if="canOpen" class="nav-item">
                <button type="button" class="btn btn-sm btn-link" @click="open">
                  <fa icon="external-link-alt" />
                  View Investigation
                </button>
              </li>
            </ul>
          </div>
        </div>
        <div class="row d-flex justify-content-between">
          <div>
            <p class="mb-0 mt-2">
              <em>{{ investigation.description }}</em>
            </p>
          </div>
        </div>
      </div>
    </div>
    <div v-if="!isExpanded" class="card-body">
      <div class="container-fluid">
        <div class="row">
          <div class="col">
            <ul class="list-group list-group-horizontal">
              <li class="list-group-item"><b>Created By:</b> {{ investigation.createdByUserFullName || "---" }}</li>
              <li class="list-group-item"><b>Created On:</b> {{ createdDate }}</li>
              <li class="list-group-item"><b>Case Type:</b> {{ investigation.caseTypeCategoryDisplayName }}</li>
              <li class="list-group-item"><b>Completed:</b> {{ completedPercent }}</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
    <div v-if="isExpanded" class="card-body">
      <div class="container-fluid">
        <div class="row">
          <div class="col-12 col-md-6 col-lg-8">
            <div class="row">
              <div class="col-12 col-md-12 col-lg-6">
                <ul class="list-group list-group-flush">
                  <li class="list-group-item"><b>Created By:</b> {{ investigation.createdByUserFullName || "---" }}</li>
                  <li class="list-group-item"><b>Created On:</b> {{ createdDate }}</li>
                  <li class="list-group-item"><b>Investigation Id:</b> {{ investigation.referenceCode || "---" }}</li>
                </ul>
              </div>
              <div class="col-12 col-md-12 col-lg-6">
                <ul class="list-group list-group-flush">
                  <li class="list-group-item"><b>Case Type:</b> {{ investigation.caseTypeCategoryDisplayName }}</li>
                  <li class="list-group-item"><b>Collaborators:</b> {{ investigation.collaborators.length }}</li>
                  <li class="list-group-item"><b>Interviews:</b> {{ investigation.interviewCount }}</li>
                </ul>
              </div>
            </div>
          </div>
          <div class="col-12 col-md-6 col-lg-4">
            <ul class="list-group list-group-flush">
              <li class="list-group-item"><b>Closed On:</b> {{ closeDate || "---" }}</li>
              <li class="list-group-item"><b>Outcome:</b> {{ investigation.outcomeShortDescription || "---" }}</li>
              <li class="list-group-item"><b>Completed:</b> {{ completedPercent }}</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
    <div class="card-footer text-center">
      <button v-if="!isExpanded" @click="toggleExpand" type="button" class="btn btn-link btn-sm pt-0 pb-1"><fa icon="chevron-down" /> View Details</button>
      <button v-if="isExpanded" @click="toggleExpand" type="button" class="btn btn-link btn-sm pt-0 pb-1"><fa icon="chevron-up" /> Hide Details</button>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import moment from "moment";
import formatter from "@/utils/formatter";

export default {
  props: {
    organizationId: String,
    investigation: Object,
  },
  data() {
    return {
      isExpanded: false,
    };
  },
  computed: {
    ...mapGetters("role", ["isVerensicsAdministrator", "isOrganizationAdministrator", "isInvestigationViewer"]),
    formatter: () => formatter,
    isOpen() {
      return this.investigation.statusCode === "open";
    },
    isClosed() {
      return this.investigation.statusCode === "closed";
    },
    header() {
      var header = "";
      if (this.isVerensicsAdministrator) header += `${this.investigation.organizationDisplayName} - `;
      header += `${this.investigation.name}`;
      return header;
    },
    statusCssClass() {
      if (this.isOpen) return "badge-info";
      if (this.isClosed) return "badge-success";
      return "badge-secondary";
    },
    canOpen() {
      var isAuthorized = this.isVerensicsAdministrator || this.isOrganizationAdministrator || this.isInvestigationViewer;
      if (!isAuthorized) return false;
      return true;
    },
    createdDate() {
      if (!this.investigation.createdDate) return null;
      return formatter.date.default(this.investigation.createdDate);
    },
    closeDate() {
      if (!this.investigation.closeDate) return null;
      var utcDate = moment.utc(this.investigation.closeDate); // handle utc round-trip from server
      return formatter.date.default(utcDate);
    },
    completedPercent() {
      var value = this.investigation.interviewCompletedCount / this.investigation.interviewCount;
      if (isNaN(value)) value = 0;
      return formatter.number.percent(value);
    },
  },
  methods: {
    toggleExpand() {
      this.isExpanded = !this.isExpanded;
    },
    open() {
      let organizationId = this.isVerensicsAdministrator && this.investigation.organizationId ? this.investigation.organizationId : this.organizationId;
      let routeData = this.$router.resolve(`/organizations/${organizationId}/investigations/${this.investigation.investigationId}`);
      window.open(routeData.href, "_blank");
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/styles/base.scss";

.card {
  @extend .mb-0;
  @extend .bg-transparent;
  @extend .border-0;
}

.card-header {
  @extend .py-1;
  @extend .bg-transparent;
  @extend .border-bottom-0;
}

.card-body {
  @extend .px-0;
  @extend .py-2;
}

.card-footer {
  @extend .p-0;
  @extend .bg-transparent;
  @extend .border-top-0;
}

li.list-group-item {
  @extend .pl-2;
  @extend .py-1;
  @extend .my-1;
  @extend .border-0;
  @extend .bg-transparent;
}
</style>
